import {
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslations } from "use-intl";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { some, times } from "lodash-es";
import { Button } from "@mui/material";

import { HotelListHeader } from "./HotelListHeader";
import { HotelsFilters } from "./HotelsFilters";
import { useHotelSearchResults } from "@/app/lib/hooks/useHotelSearchResults";
import { ErrorPanel } from "../ErrorPanel";
import { IconHotelCrossed } from "@/app/icons";
import { useHotelFiltersContext } from "@/app/lib/context/HotelFiltersContext";
import { useHotelSearchDataContext } from "@/app/lib/context/HotelSearchDataContext";
import { getNumberOfBookingNights } from "@/app/lib/utils/getNumberOfBookingNights";
import { useHotelSortContext } from "@/app/lib/context/HotelSortContext";
import { rudderAnalytics, HotelTrackingEvents } from "@/app/lib/rudderstack";
import { Drawer, DrawerTopBar } from "../Drawer";
import { SwipeableDrawer } from "../SwipeableDrawer";
import { useHotelSearch } from "@/app/lib/hooks/useHotelSearch";
import { BOARD_BASIS } from "@/app/lib/fallbackHotelsFiltersValues";
import { HotelCardList } from "./HotelCardList";
import { HotelCardSkeleton } from "./HotelCard";
import { HotelsListDrawerComponent } from "./types";
import { SearchByNameDrawer } from "./SearchByNameDrawer";
import { HotelClassFilterDrawer } from "./HotelClassFilterDrawer";
import { HotelsSortDrawer } from "./HotelsSortDrawer";
import { NoFilteringResults } from "../NoFilteringResults";
import { HotelsCalculator } from "../hotels/HotelsCalculator";
import { SearchResultsWhatsAppBanner } from "../WhatsAppBanners";
import { getDateString } from "@/app/lib/utils/dates";
import { HOTEL_CLASSES } from "@/app/lib/constants";
import { useHotelSearchForm } from "@/app/lib/hooks/useHotelSearchForm";
import type { THotelFormValues } from "@/app/lib/types/types";

function HotelsList() {
  const t = useTranslations();
  const [drawerComponent, setDrawerComponent] =
    useState<HotelsListDrawerComponent | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const { sortValue, updateSortValue } = useHotelSortContext();
  const { defaultFilters, filters, activeFilters, updateFilters } =
    useHotelFiltersContext();
  const { searchData } = useHotelSearchDataContext();
  const {
    searchResultKey,
    searchResultToken,
    progress,
    progressData,
    isFetching,
  } = useHotelSearch();
  const { handleSubmit } = useHotelSearchForm();

  const isProgressComplete = progress === 100;
  const isNoOffersFound =
    isProgressComplete && (progressData?.countOffers ?? 0) === 0;
  const areFiltersDirty = some(activeFilters, Boolean) || searchQuery;

  // remember latest refs of functions to be able to omit them
  // in effect deps array
  const savedUpdateFilters = useRef(updateFilters);
  useEffect(() => {
    savedUpdateFilters.current = updateFilters;
  }, [updateFilters]);

  // using useLayoutEffect to avoid UI flickering
  useLayoutEffect(() => {
    if (!filters && isProgressComplete && progressData) {
      savedUpdateFilters.current({
        priceRange: [progressData.minPrice, progressData.maxPrice],
        minPrice: progressData.minPrice,
        maxPrice: progressData.maxPrice,
        boardBasis: BOARD_BASIS,
        starRating: HOTEL_CLASSES,
      });
    }
  }, [
    filters,
    isProgressComplete,
    progressData,
    searchData?.dates.checkIn,
    searchData?.dates.checkOut,
  ]);

  const {
    data: searchResultData,
    isLoading: isLoadingData,
    isFetching: isFetchingData,
    isError,
    hasReachedEnd,
    isEmpty: isSearchResultEmpty,
    fetchNextPage,
  } = useHotelSearchResults({
    searchResultKey,
    searchResultToken,
    filters,
    shouldFetch: isProgressComplete && !isNoOffersFound,
    sortValue,
    hotelName: searchQuery || undefined,
    totalOffers: progressData?.countOffers,
  });

  const isEmpty = isNoOffersFound || isSearchResultEmpty;

  const [sentryRef] = useInfiniteScroll({
    loading: isFetchingData,
    hasNextPage: !hasReachedEnd,
    onLoadMore: () => {
      rudderAnalytics.track(HotelTrackingEvents.USER_SEARCHED_FOR_MORE_OFFERS);
      fetchNextPage();
    },
    disabled: isError,
    rootMargin: "0px 0px 40px 0px",
  });

  const isLoading = isFetching || isLoadingData;

  const sendNoHotelResultsFoundEvent = useEffectEvent(() => {
    if (areFiltersDirty || !searchData?.destination) return;
    const travellersCount = searchData.rooms.reduce(
      (acc, v) => acc + v.adults + v.children,
      0,
    );

    rudderAnalytics.track(HotelTrackingEvents.NO_HOTEL_RESULTS_FOUND, {
      destinationId: searchData.destination.id,
      destinationName: searchData.destination.name,
      dateFrom: getDateString(searchData.dates.checkIn),
      dateTo: getDateString(searchData.dates.checkOut),
      travellers: travellersCount,
    });
  });

  const handleSearchSubmit = (values: THotelFormValues) => {
    handleSubmit(values);
    setDrawerComponent(null);
  };

  useEffect(() => {
    if (isEmpty) {
      sendNoHotelResultsFoundEvent();
    }
  }, [isEmpty]);

  return (
    <div className="flex grow flex-col px-4">
      <HotelListHeader
        onFilterButtonClick={(drawerComponent) => {
          setDrawerComponent(drawerComponent);
        }}
        selectedSortValue={sortValue}
        progress={isLoading && progress < 100 ? progress : undefined}
        activeFilters={{ ...activeFilters, searchByName: !!searchQuery }}
        onSearchDrawerOpen={() =>
          setDrawerComponent(HotelsListDrawerComponent.SEARCH)
        }
      />

      {!isLoading && isEmpty ? (
        <div className="mt-12 flex grow flex-col items-center justify-between gap-8">
          {areFiltersDirty ? (
            <NoFilteringResults
              scope="hotels"
              title={t("hotels.filters.noHotelsMatchFilters")}
              onClearFilterClick={() => {
                updateFilters(defaultFilters);
                setSearchQuery("");
              }}
            />
          ) : (
            <ErrorPanel
              icon={IconHotelCrossed}
              primaryText={t("hotels.emptyState")}
              secondaryText={t("hotels.emptyStateDesc")}
            >
              <Button
                onClick={(e) => {
                  setDrawerComponent(HotelsListDrawerComponent.SEARCH);
                  e.currentTarget.blur();
                }}
                variant="outlined"
                color="primary"
                size="small"
                sx={{ borderRadius: "var(--radius-2xl)" }}
              >
                {t("editSearch")}
              </Button>
            </ErrorPanel>
          )}

          <SearchResultsWhatsAppBanner />
        </div>
      ) : (
        <div className="w-full">
          {isLoading ? (
            <ul className="space-y-2">
              {times(3, (index) => (
                <li key={index}>
                  <HotelCardSkeleton />
                </li>
              ))}
            </ul>
          ) : (
            <HotelCardList
              hotels={searchResultData?.pages?.flatMap((hotels) => hotels.data)}
              bottomSlot={
                isFetching || !hasReachedEnd
                  ? times(3, (index) => (
                      <li
                        key={index}
                        ref={index === 0 ? sentryRef : undefined}
                        aria-hidden={!isFetching}
                      >
                        <HotelCardSkeleton />
                      </li>
                    ))
                  : null
              }
            />
          )}
        </div>
      )}

      <HotelsSortDrawer
        open={drawerComponent === HotelsListDrawerComponent.SORT}
        value={sortValue}
        onSubmit={(newSortValue) => {
          updateSortValue(newSortValue);
          setDrawerComponent(null);
        }}
        onClose={() => setDrawerComponent(null)}
      />
      <HotelClassFilterDrawer
        open={drawerComponent === HotelsListDrawerComponent.HOTEL_CLASS}
        onCloseClick={() => setDrawerComponent(null)}
        defaultFilter={defaultFilters?.starRating}
        value={filters?.starRating}
        onSubmit={(newValue) =>
          updateFilters((prev) =>
            prev ? { ...prev, starRating: newValue } : null,
          )
        }
      />
      <SearchByNameDrawer
        open={drawerComponent === HotelsListDrawerComponent.SEARCH_BY_NAME}
        value={searchQuery}
        onClose={() => setDrawerComponent(null)}
        onSubmit={(query) => {
          setSearchQuery(query);
          setDrawerComponent(null);
        }}
      />
      <SwipeableDrawer
        open={drawerComponent === HotelsListDrawerComponent.ALL_FILTERS}
        onClose={() => setDrawerComponent(null)}
        hasFullHeight
      >
        {/* need to reset the state when drawer is open/closed */}
        {drawerComponent === HotelsListDrawerComponent.ALL_FILTERS &&
        defaultFilters &&
        filters ? (
          <HotelsFilters
            defaultFilters={defaultFilters}
            filters={filters}
            sortValue={sortValue}
            bookingNights={getNumberOfBookingNights({
              checkIn: searchData?.dates.checkIn,
              checkOut: searchData?.dates.checkOut,
            })}
            onFiltersChange={(newFilters, newSortValue) => {
              updateFilters((prevFilters) =>
                prevFilters
                  ? {
                      ...prevFilters,
                      ...newFilters,
                    }
                  : null,
              );
              updateSortValue(newSortValue);
              setDrawerComponent(null);
            }}
            onClose={() => {
              setDrawerComponent(null);
            }}
          />
        ) : null}
      </SwipeableDrawer>
      <Drawer
        open={drawerComponent === HotelsListDrawerComponent.SEARCH}
        onClose={() => setDrawerComponent(null)}
      >
        <DrawerTopBar
          onCloseClick={() => setDrawerComponent(null)}
          title={t("search")}
        />

        <HotelsCalculator
          submitButtonContent={t("confirm")}
          initialValues={searchData}
          showErrorsInline
          onSubmit={handleSearchSubmit}
        />
      </Drawer>
    </div>
  );
}

export default HotelsList;
